import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface GratRemainderCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  remainder?: EstateWaterfall_TransferLogItemFragment;
  fundingTransfer?: EstateWaterfall_TransferLogItemFragment;
  associatedAnnuities?: EstateWaterfall_TransferLogItemFragment[];
}

export function createGratRemainderCard({
  sourceEntityName,
  targetEntityName,
  remainder,
  fundingTransfer,
  associatedAnnuities = [],
}: GratRemainderCardParams): CardConfig {
  const gratName = sourceEntityName;
  const entityName = targetEntityName;

  const isHypothetical = fundingTransfer?.isHypothetical;

  let fundingAmount = fundingTransfer?.finalTransferAmount;

  // Calculate total annuities
  const totalAnnuities = associatedAnnuities.reduce(
    (sum, payment) => sum.plus(payment.finalTransferAmount || 0),
    new Decimal(0)
  );

  // Get remainder value
  const remainderValue = remainder?.finalTransferAmount || new Decimal(0);

  const gratTerm = (() => {
    const grat = remainder?.associatedObject;
    if (!grat) {
      return null;
    }

    if (grat.__typename === 'HypotheticalGRAT') {
      return grat.simulatedGrat.gratTrust?.termDurationYears;
    }

    if (
      grat.__typename === 'Entity' &&
      grat.subtype.__typename === 'GRATTrust'
    ) {
      fundingAmount = grat.subtype.initialFundingValue;

      return grat.subtype.termDurationYears;
    }

    return null;
  })();

  const termEndDate = (() => {
    if (gratTerm) {
      return new Date().getFullYear() + gratTerm;
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`grat-remainder-${sourceEntityName}-${targetEntityName}`}
        title={
          isHypothetical ? 'Hypothetical GRAT remainder' : 'GRAT remainder'
        }
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`Remaining balance from ${gratName} distributed to ${entityName} at term end.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          totalAnnuities.gt(0)
            ? {
                label: 'Total annuities',
                value: formatCurrencyNoDecimals(totalAnnuities),
              }
            : null,
          termEndDate
            ? {
                label: 'Term end',
                value: termEndDate.toString(),
              }
            : null,
          remainderValue
            ? {
                label: 'Remainder',
                value: formatCurrencyNoDecimals(remainderValue),
              }
            : null,
        ])}
      />
    ),
    label: 'GRAT remainder',
    value: formatCurrencyNoDecimals(remainderValue),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
